/** Ready (phase `ready`) and the import before it (`importing`): the receipt that shows what the
 *  account holds — the company, the tours, the bookings (sample, or date-range imports the client can
 *  open with their log and files), the team and resources read from the booking system — and the
 *  go-live block (GoLivePanel). An import that could not finish can be let go ("Finish without these
 *  tours"). */
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import * as React from 'react';

import { backgroundTaskActionsRef } from '../bg_tasks/BackgroundTaskProvider';
import useConfirmDialog from '../components/useConfirmDialog';
import type { OnboardingApi } from './api';
import { rangeLabel } from './bookingRange';
import { ErrorLine, LaneRow, LaneStateIcon, Section } from './components';
import { BOOKING_SYNC_STATES } from './labels';
import { countryName, plural } from './products';
import type { OnboardingBookingSync, OnboardingLaneState, OnboardingState } from './types';

/** Tours listed by name on the receipt before "and N more". */
export const RECEIPT_TOURS = 8;

const AUX_WORDS: Array<[keyof OnboardingState['results']['aux'], string, string]> = [
    ['guides', 'guide', 'guides'],
    ['drivers', 'driver', 'drivers'],
    ['pickup_points', 'pickup point', 'pickup points'],
    ['ticket_types', 'ticket type', 'ticket types'],
    ['custom_fields', 'custom field', 'custom fields'],
];

/** "2 cancelled · 1 no ticket type": why sample bookings were skipped, counted by reason. */
export function skippedWords(skipped: OnboardingState['results']['skipped']): string {
    const counts = new Map<string, number>();
    skipped.forEach((s) => counts.set(s.reason, (counts.get(s.reason) || 0) + 1));
    return Array.from(counts.entries()).map(([reason, n]) => `${n} ${reason}`).join(' · ');
}

/** A date-range import's state as a lane icon (the words beside it say the state). */
const SYNC_ICONS: Record<string, OnboardingLaneState> = { waiting: 'pending', running: 'running', done: 'done', failed: 'error', cancelled: 'skipped' };

/** One date-range import: its dates, its state and, once its booking sync exists, the task's log and files. */
function BookingSyncRow({ sync }: { sync: OnboardingBookingSync }) {
    const { task_id: taskId } = sync;
    return (
        <Box component="li" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1 }}>
            <LaneStateIcon state={SYNC_ICONS[sync.state] || sync.state} size={16} />
            <Typography variant="body2">{rangeLabel(sync.from, sync.to)}</Typography>
            <Typography variant="body2" color="text.secondary">{BOOKING_SYNC_STATES[sync.state] || sync.state}</Typography>
            {taskId && (
                <Button size="small" onClick={() => backgroundTaskActionsRef.current?.openDialogForTask(taskId)}>Log and files</Button>
            )}
        </Box>
    );
}

const ReceiptRow = ({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) => (
    <Box data-testid={testId} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0,1fr)' }, columnGap: 2, rowGap: 0.25, py: 1 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
    </Box>
);

/** What the account holds now. */
export function Receipt({ state }: { state: OnboardingState }) {
    const { company, results, selection } = state;
    const facts = [company.timezone, company.currency, countryName(company.country)].filter(Boolean).join(' · ');
    const aux = AUX_WORDS.filter(([key]) => results.aux[key] > 0).map(([key, one, many]) => plural(results.aux[key], one, many));
    const tours = results.tours;
    const syncs = results.booking_syncs ?? [];
    return (
        <Section title="What's in your account" testId="receipt">
            <ReceiptRow label="Company" testId="receipt-company">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{company.name}</Typography>
                {company.url && <Typography variant="body2" color="text.secondary">{company.url}</Typography>}
                {facts && <Typography variant="body2" color="text.secondary">{facts}</Typography>}
            </ReceiptRow>
            <Divider />
            <ReceiptRow label="Tours" testId="receipt-tours">
                {tours.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {selection.finished ? 'No tours imported yet — you can add them later.' : 'No tours yet.'}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body2">{plural(tours.length, 'tour')}</Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {tours.slice(0, RECEIPT_TOURS).map((t) => (
                                <Typography key={t.id} component="li" variant="body2" color="text.secondary">{t.name}</Typography>
                            ))}
                        </Box>
                        {tours.length > RECEIPT_TOURS && (
                            <Typography variant="body2" color="text.secondary">{`and ${tours.length - RECEIPT_TOURS} more`}</Typography>
                        )}
                    </>
                )}
            </ReceiptRow>
            <Divider />
            <ReceiptRow label={syncs.length ? 'Bookings' : 'Sample bookings'} testId="receipt-bookings">
                <Typography variant="body2">
                    {results.bookings_skipped
                        ? `${plural(results.bookings_imported, 'booking')} imported · ${results.bookings_skipped} skipped`
                        : `${plural(results.bookings_imported, 'booking')} imported`}
                </Typography>
                {results.skipped.length > 0 && (
                    <Typography variant="body2" color="text.secondary">{`Skipped: ${skippedWords(results.skipped)}`}</Typography>
                )}
                {syncs.length > 0 && (
                    <Box component="ul" aria-label="Imports by date range" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0 }}>
                        {syncs.map((sync, i) => <BookingSyncRow key={sync.task_id || `waiting-${i}`} sync={sync} />)}
                    </Box>
                )}
            </ReceiptRow>
            <Divider />
            <ReceiptRow label="Team and resources" testId="receipt-aux">
                <Typography variant="body2" color={aux.length ? 'text.primary' : 'text.secondary'}>
                    {aux.length ? aux.join(' · ') : 'None read from your booking system yet'}
                </Typography>
            </ReceiptRow>
        </Section>
    );
}

/** The import of the chosen tours and their sample bookings; when it could not finish, the way on. */
export function ImportingPanel({ state, api, canWrite, afterAction, stalled }: {
    state: OnboardingState;
    api: OnboardingApi;
    canWrite: boolean;
    afterAction: () => void;
    stalled: boolean;
}) {
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const [confirm, confirmDialog] = useConfirmDialog() as unknown as [(o: any) => Promise<boolean>, React.ReactNode];
    const lanes = state.lanes.filter((l) => l.key === 'tours' || l.key === 'sample_bookings');
    const n = state.selection.product_ids.length;
    // The running selection's date range: its import is the newest one not ended yet.
    const ranged = (state.results.booking_syncs ?? []).filter((s) => s.state === 'waiting' || s.state === 'running').pop();

    const finish = async () => {
        const ok = await confirm({
            title: 'Finish without these tours?',
            body: 'Your account moves on to Ready with what was imported so far. You can add tours later.',
            okButtonText: 'Finish',
        });
        if (!ok) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await api.finish();
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
            afterAction();
        }
    };

    return (
        <Section title={stalled ? "The import didn't finish" : `Importing ${plural(n, 'tour')}`} testId="importing">
            {confirmDialog}
            <Stack spacing={1}>
                {!stalled && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="body2" color="text.secondary">
                            {ranged
                                ? `We're importing your tours, then their bookings from ${rangeLabel(ranged.from, ranged.to)}. A long range can take a while.`
                                : "We're importing your tours, then a few of their bookings. This usually takes a few minutes."}
                        </Typography>
                    </Stack>
                )}
                {lanes.map((lane) => <LaneRow key={lane.key} lane={lane} />)}
                {stalled && (
                    <Alert severity="warning" data-testid="import-stalled">
                        Our team is looking at it. You can finish without these tours and add them later.
                    </Alert>
                )}
                <ErrorLine error={error} testId="finish-error" />
                {stalled && canWrite && (
                    <Box>
                        <Button variant="outlined" disabled={busy} onClick={finish}>Finish without these tours</Button>
                    </Box>
                )}
            </Stack>
        </Section>
    );
}
